import { useEffect, useRef, useState } from "react";
import { intlFormat, makeDateString } from "../../helpers/helpers";
import {
  fetchUsers,
  fetchWaBlacklists,
  fetchWaLogs,
} from "../../helpers/fetchers";
import { useAppState } from "../../components/AppProvider/AppProvider";
import { User } from "../../model";

const WALogPage = () => {
  const [ctx, dispatch] = useAppState() ?? [];
  const from = useRef(`${makeDateString(new Date())}T00:00`);
  const to = useRef(`${makeDateString(new Date())}T23:59`);
  const [, refresh] = useState(false);
  const waLogs = useRef([] as any[]);
  const users = useRef([] as User[]);
  const blacklists = useRef([] as any[]);

  const render = () => {
    refresh((n) => !n);
  };

  const fetchWaBlacklistsData = async () => {
    const d = await fetchWaBlacklists({ apiKey: ctx?.apiKey ?? "" });

    if (d) {
      blacklists.current = d;
      render();
    }
  };

  const fetchWaLogsData = async () => {
    const d = await fetchWaLogs({
      apiKey: ctx?.apiKey ?? "",
      from: new Date(from.current).toISOString(),
      to: new Date(to.current).toISOString(),
    });

    if (d) {
      waLogs.current = d;
      render();
    }
  };

  const fetchUsersData = async () => {
    const d = await fetchUsers(
      process.env.REACT_APP_BASE_URL ?? "",
      ctx?.apiKey ?? "",
      { showHidden: true }
    );

    if (d) {
      users.current = d;
      render();
    }
  };

  const handleInit = async () => {
    fetchWaLogsData();
    fetchUsersData();
    fetchWaBlacklistsData();
  };

  useEffect(() => {
    handleInit();
  }, []);

  const usersMapped: { [key: string]: User } = {};

  users.current.forEach((u) => {
    usersMapped[u.id ?? ""] = u;
  });

  const waLogsSources =
    waLogs?.current?.map((w) => {
      return w?.source;
    }) ?? [];
  const uniqueSources = [...new Set(waLogsSources)];

  const renderSentStatus = (isSent?: boolean) => {
    const statusClass = isSent ? "text-success" : "text-danger";
    const label = isSent ? "Passed" : "Rejected";

    return <div className={`font-weight-bold ${statusClass}`}>{label}</div>;
  };

  const mappedSources: { [key: string]: any } = {};

  uniqueSources.forEach((s) => {
    mappedSources[s] = waLogs.current.filter((w) => w?.source === s);
  });

  const sourceFilters = useRef([] as string[]);
  const blacklistNames = new Set(
    blacklists?.current?.map((b: any) => b?.name) ?? []
  );
  const isBlacklistedSource = (source?: string) =>
    source ? blacklistNames.has(source) : false;
  const filteredWaLogs =
    waLogs?.current?.filter((w) => !isBlacklistedSource(w?.source)) ?? [];
  const initialLogCounts = {
    sent: 0,
    sentWhatsapp: 0,
    sentTelegram: 0,
    sentEmail: 0,
    rejected: 0,
    rejectedWhatsapp: 0,
    rejectedTelegram: 0,
    rejectedEmail: 0,
  };

  const logCounts =
    filteredWaLogs?.reduce((acc, w) => {
      const recipientCount = w?.whatsappLogUserIds?.length ?? 0;

      const sentWhatsapp = w?.sent ? recipientCount : 0;
      const sentTelegram = w?.sentTelegram ? recipientCount : 0;
      const sentEmail = w?.sentEmail ? recipientCount : 0;

      const rejectedWhatsapp = w?.sent ? 0 : recipientCount;
      const rejectedTelegram = w?.sentTelegram ? 0 : recipientCount;
      const rejectedEmail = w?.sentEmail ? 0 : recipientCount;

      acc.sentWhatsapp += sentWhatsapp;
      acc.sentTelegram += sentTelegram;
      acc.sentEmail += sentEmail;

      acc.rejectedWhatsapp += rejectedWhatsapp;
      acc.rejectedTelegram += rejectedTelegram;
      acc.rejectedEmail += rejectedEmail;

      acc.sent += sentWhatsapp + sentTelegram + sentEmail;
      acc.rejected += rejectedWhatsapp + rejectedTelegram + rejectedEmail;

      return acc;
    }, { ...initialLogCounts }) ?? initialLogCounts;
  const totalMessages = filteredWaLogs?.length ?? 0;
  const blacklistedMessages = Math.max(
    (waLogs?.current?.length ?? 0) - totalMessages,
    0
  );

  return (
    <>
      <div className="m-3">
        <div>
          <strong>WA Log</strong>
        </div>
        <div>
          <hr className="border border-dark" />
        </div>
        <div className="d-flex">
          <div>
            <div>From</div>
            <div>
              <input
                type="datetime-local"
                className="form-control form-control-sm"
                defaultValue={from.current}
                onBlur={(e) => {
                  from.current = e.target.value;
                }}
              />
            </div>
          </div>

          <div>
            <div>To</div>
            <div>
              <input
                type="datetime-local"
                className="form-control form-control-sm"
                defaultValue={to.current}
                onBlur={(e) => {
                  to.current = e.target.value;
                }}
              />
            </div>
          </div>

          <div>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                fetchWaLogsData();
              }}
            >
              Get Data
            </button>
          </div>
        </div>

        <div className="d-flex">
          <ol>
            {Object.entries(mappedSources).map((d) => {
              return (
                <>
                  <li>
                    <div className="d-flex">
                      <div>
                        <strong
                          className={`${
                            sourceFilters.current.find((s) => s === d?.[0])
                              ? `text-info`
                              : ``
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            const foundSourceFilter =
                              sourceFilters.current.find((s) => s === d?.[0]);

                            if (!foundSourceFilter) {
                              sourceFilters.current = [
                                ...sourceFilters.current,
                                d?.[0],
                              ];
                            } else {
                              sourceFilters.current =
                                sourceFilters.current.filter(
                                  (s) => s !== d?.[0]
                                );
                            }

                            render();
                          }}
                        >
                          {d?.[0]}
                        </strong>
                      </div>
                      <div className="mx-1">({d?.[1]?.length} messages)</div>
                      <div className="mx-1">
                        (Total Sent{" "}
                        {d?.[1]?.reduce(
                          (acc: any, w: any) =>
                            acc + (w?.whatsappLogUserIds?.length ?? 0),
                          0.0
                        )}
                        )
                      </div>

                      <div className="mx-2">
                        {blacklists.current.find((b) => b?.name === d?.[0]) ? (
                          <>
                            <div className="font-weight-bold text-danger">
                              Blacklisted
                            </div>
                          </>
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>
                  </li>
                </>
              );
            })}
          </ol>
        </div>

        <div>
          <b>Total:</b> {totalMessages} messages, 
          &nbsp;<b>Sent:</b> {logCounts.sent}
          &nbsp;(Whatsapp: {logCounts.sentWhatsapp}, 
          Telegram: {logCounts.sentTelegram}, 
          Email: {logCounts.sentEmail}
          ),
          &nbsp;<b>Rejected:</b> {logCounts.rejected}
          &nbsp;(Whatsapp: {logCounts.rejectedWhatsapp}, 
          Telegram: {logCounts.rejectedTelegram}, 
          Email: {logCounts.rejectedEmail}
          ),
          &nbsp;<b>Blacklisted:</b> {blacklistedMessages} messages
        </div>

        <div
          style={{ resize: "vertical", height: "75vh" }}
          className="border border-dark overflow-auto"
        >
          <table
            className="table table-sm"
            style={{ borderCollapse: "separate" }}
          >
            <tr>
              {
                [
                  "#",
                  "Message",
                  "Source",
                  "Recipient",
                  "Timestamp",
                  "Sent",
                  "Sent Telegram",
                  "Sent Email",
                ].map((h) => {
                  return (
                    <>
                      <th
                        className="bg-dark text-light"
                        style={{ position: "sticky", top: 0 }}
                      >
                        {h}
                      </th>
                    </>
                  );
                })
              }
            </tr>
            {waLogs?.current
              .filter((w) => {
                if (sourceFilters.current.length > 0) {
                  return sourceFilters.current.find((s) => s === w?.source);
                } else {
                  return true;
                }
              })
              .map((w, i) => {
                return (
                  <>
                    <tr>
                      <td className="border border-dark">{i + 1}</td>
                      <td className="border border-dark">{w?.message}</td>
                      <td className="border border-dark">{w?.source}</td>
                      <td className="border border-dark">
                        {w?.whatsappLogUserIds
                          ?.map((wu: any) => {
                            const foundUser = usersMapped[wu?.userId ?? ""];
                            return foundUser?.name ?? wu?.recipient;
                          })
                          .join(", ")}
                      </td>
                      <td className="border border-dark">
                        {intlFormat({
                          date: w?.createdAt,
                          dateStyle: "medium",
                          timeStyle: "medium",
                        })}
                      </td>
                      <td className="border border-dark">
                        {renderSentStatus(w?.sent)}
                      </td>
                      <td className="border border-dark">
                        {renderSentStatus(w?.sentTelegram)}
                      </td>
                      <td className="border border-dark">
                        {renderSentStatus(w?.sentEmail)}
                      </td>
                    </tr>
                  </>
                );
              })}
          </table>
        </div>
      </div>
    </>
  );
};
export default WALogPage;
